import type { SxStyleProp } from '@rover/kibble/core/styledSystemPropTypes';
import { ThemeRadii } from '@rover/kibble/styles';

import { breakpoints } from '../../themes/themeConstants';
import { MarginSystemProps } from '../styledSystemPropTypes';

/** Responsive image sources array for srcset [mobile, tablet, desktop] */
export type SrcsetArray =
  | [string, string, string]
  | [null, string, string]
  | [string, null, string]
  | [string, string, null];

/** Base props shared across all platforms (no conflicting props) */
type ImageCommonProps = MarginSystemProps & {
  /** description of image content, required for accessibility */
  description: string;
  /** Image height in pixels or CSS units */
  height: string | number;
  /** Image width in pixels or CSS units */
  width: string | number;
  /** How image should resize to fit container */
  resizeMethod?: 'cover' | 'contain' | 'center';
  /** Border radius from theme tokens */
  borderRadius?: ThemeRadii;
  /** Callback fired when image loads successfully */
  onLoad?: (event: onLoadProps) => void;
  /** Image source URL (universal fallback) */
  src?: string;
  /** ID for finding this element in unit tests */
  testID?: string;
  /** Theme-aware custom style overrides */
  sx?:
    | SxStyleProp
    | Record<string, string | number | undefined | Array<string | number | undefined>>;
};

/** Web-specific props with their own src definition */
export type ImageWebOnlyProps = {
  /** Image source - string URL or responsive srcset array */
  src?: string | SrcsetArray;
  /** Media query sizes attribute for responsive images */
  sizes?: string | string[];
  /** Custom width descriptors for srcset (defaults to 767w, 991w, 1440w) */
  srcsetWidths?: SrcsetArray;
  /** Load image eagerly instead of lazy loading */
  eager?: boolean;
};

/** React Native-specific props with their own src definition */
export type ImageRxnOnlyProps = {
  /** Image source - string URL or require() asset number */
  src?: string | number;
  /** Blur radius for placeholder effect */
  blurRadius?: number;
};

/**
 * Unified props for hybrid/cross-platform usage
 * Contains both web and React Native specific props as optional
 */
export type ImageProps = ImageCommonProps & {
  /** Contains all Web-specific props, and serves as Web-specific override for all ImageCommonProps that are relevant on Web */
  webProps?: Partial<Omit<ImageCommonProps, keyof ImageWebOnlyProps>> & ImageWebOnlyProps;
  /** Contains all RxN-specific props, and serves as RxN-specific override for all ImageCommonProps that are relevant on RxN */
  rxnProps?: Partial<Omit<ImageCommonProps, keyof ImageRxnOnlyProps | 'borderRadius'>> &
    ImageRxnOnlyProps;
};

export const NOT_FORWARDED_PROPS = [
  'borderRadius',
  'marginTop',
  'eager',
  'webSizes',
  'webSrcsetWidths',
  'webSrc',
  'rxnSrc',
  'rxnBlurRadius',
  'sx',
];

/** Event data passed to onLoad callback */
type onLoadProps = { width: number; height: number; uri: string };

export const BREAKPOINTS = breakpoints;

export const getSizes = (sizes: string | string[]): string => {
  if (Array.isArray(sizes)) {
    return sizes
      .map((size, index) =>
        index === sizes.length - 1 ? `${size}` : `(max-width: ${BREAKPOINTS[index]}) ${size}`
      )
      .join(', ');
  }
  return sizes;
};
